// Picking and staging that web cannot safely perform yet, so the buttons can explain themselves
// instead of silently doing nothing (placeholders) or damaging another order (shared pallets).

import { getItemPalletId } from '@/utilities/manifestPallets';
import { isPlaceholderItem } from '@/utilities/forwardStocking';
import { getPalletDisplay } from './utils';

export const NEEDS_ALLOCATION_MESSAGE =
    'This order has unallocated forward stocked items. Allocate them by scanning the proper item labels in the mobile app.';

export const MULTI_ORDER_PALLET_MESSAGE =
    "This pallet holds items for more than one order. Pull this order's items off the pallet and scan using the mobile app before picking or staging.";

export const PALLET_CHECK_FAILED_MESSAGE =
    'Could not check what else is on these pallets. Reload the page and try again.';

export const PALLET_CHECK_PENDING_MESSAGE = 'Checking what else is on this pallet...';

export const orderNeedsAllocation = (order) =>
    (order?.itemsByOrderId || []).some((item) => isPlaceholderItem(item, order));

export const palletSpansMultipleOrders = (items) =>
    new Set((items || []).map((item) => item?.order_id).filter(Boolean)).size > 1;

export const collectPalletIds = (orders) => [
    ...new Set((orders || []).flatMap((order) => (order?.manifestItems || []).map(getItemPalletId).filter(Boolean))),
];

const palletLabelsByOrders = (orders) => {
    const labels = new Map();
    (orders || []).forEach((order) => {
        (order?.manifestItems || []).forEach((item) => {
            const palletId = getItemPalletId(item);
            if (palletId && !labels.has(palletId)) {
                labels.set(palletId, getPalletDisplay(item.pallet) || palletId);
            }
        });
    });
    return labels;
};

/**
 * @param orders - the manifest's orders, unfiltered by viewer
 * @param palletSpread - rows of { item_id, pallet_id, order_id } for every pallet on the manifest
 * @param spreadFailed - true when that lookup failed; blocks everything rather than guessing
 * @param spreadPending - same, while it is still in flight
 */
export const buildPickBlockers = ({ orders, palletSpread, spreadFailed = false, spreadPending = false } = {}) => {
    const needsAllocation = (orders || [])
        .filter(orderNeedsAllocation)
        .map((order) => ({ order_id: order.order_id, label: order.order_number || order.order_id }));

    const itemsByPallet = new Map();
    (palletSpread || []).forEach((row) => {
        if (!row?.pallet_id) return;
        itemsByPallet.set(row.pallet_id, [...(itemsByPallet.get(row.pallet_id) || []), row]);
    });

    const labels = palletLabelsByOrders(orders);
    const multiOrderPallets =
        spreadFailed || spreadPending
            ? []
            : [...itemsByPallet.entries()]
                  .filter(([, items]) => palletSpansMultipleOrders(items))
                  .map(([pallet_id]) => ({ pallet_id, label: labels.get(pallet_id) || pallet_id }));

    return {
        checkFailed: spreadFailed,
        checkPending: spreadPending && !spreadFailed,
        needsAllocation,
        multiOrderPallets,
        blockedOrderIds: new Set(needsAllocation.map((entry) => entry.order_id)),
        blockedPalletIds: new Set(multiOrderPallets.map((entry) => entry.pallet_id)),
    };
};

// Allocation is checked first: it comes off the already-loaded manifest, so it stays the specific,
// actionable message even when the pallet lookup is unresolved.
export const pickBlockedReason = (blockers, { orderId, palletIds = [] } = {}) => {
    if (!blockers) return '';
    if (orderId && blockers.blockedOrderIds.has(orderId)) return NEEDS_ALLOCATION_MESSAGE;
    if (blockers.checkFailed) return PALLET_CHECK_FAILED_MESSAGE;
    if (blockers.checkPending) return PALLET_CHECK_PENDING_MESSAGE;
    if (palletIds.filter(Boolean).some((palletId) => blockers.blockedPalletIds.has(palletId))) {
        return MULTI_ORDER_PALLET_MESSAGE;
    }
    return '';
};

export const hasPickBlockers = (blockers) =>
    !!blockers && (blockers.checkFailed || !!blockers.needsAllocation.length || !!blockers.multiOrderPallets.length);
